import { ChatRole, useAppSelector, useTranslation, useUserSettings } from "@next-platform/client-common-core";
import {
  CapabilityKey,
  SubCapabilityKey,
  useApplicationCapabilities,
  useApplicationCapabilitiesAllowed,
} from "@next-platform/client-common-core/infra/application/capabilities";
import { ButtonIcon, ButtonSize, ButtonVariant, IconVariants, Shape, Text, TextVariants, Tooltip } from "@next-platform/client-common-spicy-ui";
import { JSX, useCallback, useEffect, useState } from "react";
import { useParams } from "react-router-dom";

import { Col } from "@/components/customComponents";
import { QueueStatus } from "@/constants";
import { PREFERRED_VOICE_ACTIONS } from "@/pages/Chat/components/voices/constant";
import useChatMessageAudio from "@/pages/Chat/components/voices/useChatMessageAudio";
import useGreetingAudio from "@/pages/Chat/components/voices/useGreetingAudio";
import { cn, isMessageValidForAudio } from "@/utils";

import ChatBubbleRating from "./ChatBubbleRating";
import { ChatActions, ChatBarProps, IChatBarActions, MessageType, VoiceButtonVariant } from "./types";

const ChatBar = ({
  onClickPrevious,
  onClickNext,
  count,
  message,
  sendRating,
  character,
  conversationLength,
  chatIndex,
  altMsgs,
  isClonePartial,
  removalMode,
  isTyping,
  onRegenerate,
  handlePreferredVoiceActions,
  setCharacterThatIsTyping,
  setIsMessageEditMode,
  currentAudioMessageId,
  voiceId,
  currentConversationId,
  defaultPersonaId,
  isAudioLoading,
  loadMessages,
  handleModalVisibility,
  handleVoiceButtonClick,
  showVoiceSelectionModal,
  messageEditMode,
  setMessageEditMode,
  setEditMessageContent,
  scrollToBottom,
  isChatWithGroup,
  hasPendingMessage,
  isLastMessage,
  isGroupBelowMinimum,
}: ChatBarProps): JSX.Element => {
  const { t } = useTranslation();
  const [labelSentMessage, setLabelSentMessage] = useState("");
  const [isRatingSubmitting, setIsRatingSubmitting] = useState(false);
  const isImageGenerationSettingsAllowed = useApplicationCapabilitiesAllowed(SubCapabilityKey.ChatImageImageGenerationSettings);
  const [isRatingSent, setIsRatingSent] = useState(false);
  const { status: waitingQueueStatus } = useAppSelector(({ waitingQueue }) => waitingQueue) || {};
  const { characterId } = useParams();
  const isImageEditMode = messageEditMode === "image";
  const [showRatingBox, setShowRatingBox] = useState(false);

  //if conversation length is 1, it means it is a new conversation and user can edit the greeting message
  const enableEditableChatExperience = waitingQueueStatus !== QueueStatus.WAITING || conversationLength === 1;
  const [isMessageActionDisabled, setIsMessageActionDisabled] = useState(false);
  const regeneratable =
    (altMsgs?.length ?? 0) > 0 ||
    ((chatIndex ?? 0) > 0 &&
      conversationLength - 1 === chatIndex &&
      message?.role === ChatRole.BOT &&
      !altMsgs?.length &&
      isLastMessage &&
      !isTyping);

  const allowMessageActions = regeneratable && !messageEditMode && !isTyping && enableEditableChatExperience && !isClonePartial && !removalMode;
  const isRatingBoxVisible = allowMessageActions && showRatingBox;
  const allowRating = allowMessageActions && message?.id && enableEditableChatExperience;
  const isImage = !!message?.image_url;
  const isImageEditable = isImage && !!message?.image_prompt;

  const {
    handleOnPress: handleOnPressVoice,
    chatVoiceButtonVariant,
    isAudioLoadingForCurrentMessage,
  } = useChatMessageAudio({
    currentAudioMessageId,
    messageId: message?.id,
    voiceId,
    conversationId: currentConversationId,
    characterId,
    defaultPersonaId,
    isAudioLoading,
    handlePreferredVoiceActions,
    loadMessages,
    handleModalVisibility,
    handleVoiceButtonClick,
    showVoiceSelectionModal,
  });

  const ttsCapability = useApplicationCapabilities(CapabilityKey.Tts);
  const isTtsAllowed = ttsCapability?.allowed ?? false;
  const { ttsIncludeNarration } = useUserSettings();

  const stopLiveTts = useCallback(() => {
    void handlePreferredVoiceActions({ action: PREFERRED_VOICE_ACTIONS.Stop });
  }, [handlePreferredVoiceActions]);

  const {
    isGreetingStaticRoute,
    isGreetingSignInRoute,
    isGreetingAudioPlaying,
    isGreetingIconHidden,
    handleGreetingStaticPlay,
    handleGreetingSignIn,
  } = useGreetingAudio({
    message,
    character,
    chatIndex,
    stopLiveTts,
  });

  useEffect(() => {
    if (isRatingBoxVisible || isRatingSubmitting || isImageEditMode) {
      scrollToBottom();
    }
  }, [isRatingBoxVisible, isRatingSubmitting, isImageEditMode]);

  useEffect(() => {
    setShowRatingBox(false);
  }, [conversationLength]);

  const isBotMessage = message?.role === ChatRole.BOT;
  const shouldShowVoiceButton =
    !isTyping &&
    !messageEditMode &&
    isBotMessage &&
    !message?.image_url &&
    (isTtsAllowed || ttsCapability?.isPaywall) &&
    isMessageValidForAudio(message?.content as string, ttsIncludeNarration) &&
    !isClonePartial &&
    !removalMode &&
    !isChatWithGroup;

  const totalCount = altMsgs?.length;

  const messageRating = {
    messageId: message?.id,
    rating: message?.rating,
    sendRating: sendRating,
    messageType: message?.image_url ? MessageType.IMAGE : MessageType.TEXT,
  };

  const handleRegenerate = async () => {
    const newTypingCharacter = character?.sub_characters?.find((char) => char.id === message?.character_id);
    setCharacterThatIsTyping(newTypingCharacter ?? character);

    handlePreferredVoiceActions({ action: PREFERRED_VOICE_ACTIONS.Stop });
    setIsMessageActionDisabled(true);
    const payload = {
      regenerate: true,
      image_prompt: message?.image_prompt,
      continue_chat: message?.role === ChatRole.BOT,
      subCharacterId: message?.character_id as string,
    };
    await onRegenerate(payload);
    setIsMessageActionDisabled(false);
    setCharacterThatIsTyping(null);
  };

  const handleContinueChat = async () => {
    setCharacterThatIsTyping(character);
    handlePreferredVoiceActions({ action: PREFERRED_VOICE_ACTIONS.Stop });
    setIsMessageActionDisabled(true);
    await onRegenerate({ type: "text", continue_chat: true, subCharacterId: message?.character_id as string });
    setIsMessageActionDisabled(false);
  };

  const handleImageEdit = () => {
    if (setMessageEditMode && setEditMessageContent) {
      setMessageEditMode("image");
      setEditMessageContent(message?.image_prompt ?? "");
      setIsMessageEditMode(true);
    }
  };

  const isVoiceStopIcon = isGreetingStaticRoute ? isGreetingAudioPlaying : chatVoiceButtonVariant === VoiceButtonVariant.STOP;
  const getVoicePressHandler = () => {
    if (isGreetingSignInRoute) {
      return handleGreetingSignIn;
    }
    return isGreetingStaticRoute ? () => void handleGreetingStaticPlay() : handleOnPressVoice;
  };
  const handleVoicePress = getVoicePressHandler();

  const actions = [
    {
      icon: IconVariants.REFRESH,
      isHidden: !allowMessageActions || hasPendingMessage,
      onPress: () => handleRegenerate(),
      isDisabled: isMessageActionDisabled || isGroupBelowMinimum,
      key: ChatActions.REGENERATE,
      title: t("chat:chatBar.action.regenerate"),
    },
    {
      icon: IconVariants.MESSAGE_PLUS,
      isHidden: !allowMessageActions || isChatWithGroup,
      onPress: () => handleContinueChat(),
      isDisabled: isMessageActionDisabled,
      key: ChatActions.CONTINUE_CHAT,
      title: t("chat:chatBar.action.continue"),
    },
    {
      icon: IconVariants.EDIT,
      onPress: handleImageEdit,
      isHidden: !isImageEditable || !regeneratable || !isImageGenerationSettingsAllowed,
      key: ChatActions.REGENERATE,
      isDisabled: isMessageActionDisabled,
      title: t("chat:chatBar.action.edit"),
    },
    {
      icon: IconVariants.STAR,
      isDisabled: !!message?.rating || isRatingSent || isMessageActionDisabled,
      isHidden: !allowRating || hasPendingMessage,
      isFilled: !!message?.rating || isRatingBoxVisible || isRatingSent,
      isLoading: isRatingSubmitting && !isRatingBoxVisible,
      onPress: () => setShowRatingBox(true),
      key: ChatActions.RATE_MESSAGE,
      className: "!opacity-100",
      title: t("chat:chatBar.action.rate"),
    },
    {
      icon: isVoiceStopIcon ? IconVariants.STOP_CIRCLE : IconVariants.VOLUME_2,
      isHidden: !shouldShowVoiceButton || isGreetingIconHidden,
      onPress: handleVoicePress,
      isLoading: isGreetingStaticRoute ? false : isAudioLoadingForCurrentMessage,
      key: ChatActions.VOICE,
      testId: "ChatBar-ListenButton",
      classNames: {
        icon: isVoiceStopIcon ? "fill-backgroundInvert stroke-backgroundInvert [&_rect]:!fill-background [&_rect]:!stroke-background" : "",
      },
      title: t("chat:chatBar.action.listen"),
    },
  ];

  const isPrevious = !!onClickPrevious && count && count > 1;
  const isNext = !!onClickNext && count && count < (totalCount ?? 0);
  const showPrevAndNext = totalCount && totalCount > 1;

  const isChatBarHidden = !shouldShowVoiceButton && !allowMessageActions && !allowRating;

  useEffect(() => {
    let timer: ReturnType<typeof setTimeout>;
    if (labelSentMessage) {
      timer = setTimeout(() => {
        setLabelSentMessage("");
      }, 5000);
    }
    return () => clearTimeout(timer);
  }, [labelSentMessage]);

  if (isChatBarHidden) return <></>;

  return (
    <Col className="gap-md">
      <div className="flex justify-between items-center">
        <div className="flex items-center gap-2">
          {actions?.map((action: IChatBarActions, index: number) => {
            if (action?.isHidden) {
              return null;
            }

            return (
              <Tooltip title={action?.title} key={index} offset={0}>
                <ButtonIcon
                  icon={action?.icon}
                  onClick={action?.onPress}
                  disabled={action?.isDisabled}
                  shape={Shape.PILL}
                  variant={ButtonVariant.TEXT}
                  size={ButtonSize.MEDIUM}
                  className={cn("!text-gray-11", action?.className)}
                  loading={action?.isLoading}
                  data-testid={action?.testId}
                  classNames={{
                    icon: cn(action?.isFilled ? "!fill-gray-11" : "", action?.classNames?.icon),
                  }}
                />
              </Tooltip>
            );
          })}
        </div>

        <div className="flex items-center">
          {!!showPrevAndNext && (
            <>
              <ButtonIcon
                icon={IconVariants.CHEVRON_LEFT}
                onClick={onClickPrevious}
                disabled={!isPrevious}
                size={ButtonSize.SMALL}
                variant={ButtonVariant.TEXT}
                aria-label="previous"
              />
              {!!(count && totalCount) && (
                <Text variant={TextVariants.LABEL_MEDIUM_REGULAR} className="text-gray-12">
                  {count}/{totalCount}
                </Text>
              )}

              <ButtonIcon
                icon={IconVariants.CHEVRON_RIGHT}
                onClick={onClickNext}
                disabled={!isNext}
                size={ButtonSize.SMALL}
                variant={ButtonVariant.TEXT}
                aria-label="next"
              />
            </>
          )}
        </div>
      </div>
      {showRatingBox ? (
        <ChatBubbleRating
          isSubmitting={isRatingSubmitting}
          setIsSubmitting={setIsRatingSubmitting}
          setLabelSentMessage={setLabelSentMessage}
          messageRating={messageRating}
          setShowRatingBox={setShowRatingBox}
          setIsRatingSent={setIsRatingSent}
        />
      ) : null}
      {labelSentMessage ? (
        <Text variant={TextVariants.LABEL_MEDIUM_REGULAR} className="text-gray-11">
          {labelSentMessage}
        </Text>
      ) : null}
    </Col>
  );
};

export default ChatBar;
